import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, FolderKanban, FileText, Image, Newspaper, Sparkles, Award, Mail, Heart, Contact, UserCheck, GraduationCap, IndianRupee } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge, fmtDate } from "./AFields";

const CARDS = [
  ["participants", "Participants", Users, "/superadmin/participants"],
  ["volunteers", "Volunteers", UserCheck, "/superadmin/participants"],
  ["interns", "Interns", GraduationCap, "/superadmin/participants"],
  ["projects", "Projects", FolderKanban, "/superadmin/collections/projects"],
  ["articles", "Articles", FileText, "/superadmin/collections/articles"],
  ["gallery", "Gallery Items", Image, "/superadmin/collections/gallery"],
  ["news", "News & Events", Newspaper, "/superadmin/collections/news"],
  ["greetings", "Greeting Studio", Sparkles, "/superadmin/greetings"],
  ["certificates", "Certificates", Award, "/superadmin/participants?tab=certificates"],
  ["new_enquiries", "New Enquiries", Mail, "/superadmin/enquiries"],
  ["donations", "Donations", Heart, "/superadmin/donations"],
  ["contacts", "Greeting Contacts", Contact, "/superadmin/greetings/contacts"],
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="text-sm text-slate-500">Loading dashboard…</p>;

  return (
    <div data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">Overview of Sahaj Sarathi Foundation's digital portal.</p>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
        {CARDS.map(([key, label, Icon, to]) => (
          <Link key={key} to={to} data-testid={`stat-${key}`}
            className="rounded-lg border border-gray-200 bg-white p-5 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
              <Icon size={17} className="text-saffron-600" strokeWidth={1.8} />
            </div>
            <p className="mt-2 font-display text-3xl font-bold text-navy">{data.stats[key] ?? 0}</p>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Recent Enquiries</h2>
          <ul className="mt-4 space-y-3">
            {data.recent_enquiries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0"><p className="truncate font-semibold text-navy">{e.name}</p><p className="text-xs text-slate-400">{e.ref_no} · {fmtDate(e.created_at)}</p></div>
                <StatusBadge value={e.status} />
              </li>
            ))}
            {data.recent_enquiries.length === 0 && <li className="text-sm text-slate-400">No enquiries yet.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Recent Donations</h2>
          <ul className="mt-4 space-y-3">
            {data.recent_donations.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0"><p className="truncate font-semibold text-navy">{d.name}</p><p className="text-xs text-slate-400"><IndianRupee size={10} className="inline" />{Number(d.amount).toLocaleString("en-IN")} · {fmtDate(d.created_at)}</p></div>
                <StatusBadge value={d.status} />
              </li>
            ))}
            {data.recent_donations.length === 0 && <li className="text-sm text-slate-400">No donations yet.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Recent Activity</h2>
          <ul className="mt-4 space-y-2.5">
            {data.recent_activity.map((a) => (
              <li key={a.id} className="text-xs text-slate-500">
                <span className="font-semibold text-navy">{a.actor}</span> {a.action} → {a.entity} <span className="text-slate-400">· {fmtDate(a.at)}</span>
              </li>
            ))}
            {data.recent_activity.length === 0 && <li className="text-sm text-slate-400">No activity yet.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
